import React, { ReactElement, ReactNode, useEffect } from 'react';
import { Content } from 'idsk-react-components';
import {
  BackLink, Breadcrumbs, getTitle, NotFoundPage, PageLoader, setTitle, waitForElement,
} from 'slovlex-idsk-react-components';
import { useLocation, useParams } from 'react-router';
import PripomienkyNavigation from './components/PripomienkyNavigation/PripomienkyNavigation';
import GlobalErrors from '../../module/GlobalErrors';
import Uuid from '../../model/Uuid';
import routes from '../../routing/routes';
import { BreadcrumbRouteProps } from '../../types/BreadcrumbRouteProps';
import getBreadcrumbPrefixArray from '../../function/getBreadcrumbPrefixArray';
import { useHistoryContext } from '../../context/HistoryContext';
import './layout.scss';
import { useUserContext } from '../../context/UserContext';
import ErrorMessages from '../../module/ErrorMessages';
import useFetchHistoria from '../../hooks/useFetchHistoria';
import { StadiumMaterialuEnum } from '../../model/StadiumMaterialu';
import hasBeenInStadium from '../../function/hasBeenInStadium';
import { useStadiumMaterialuHistoriaContext } from '../../context/StadiumMaterialuHistoriaContext';
import SuccessMessages from '../../module/SuccessMessages';

interface Props {
  breadcrumbRouteList?: Array<BreadcrumbRouteProps>;
  children: ReactNode;

  reloadSubjektBezPripomienokList?(): void;

  reloadPripomienkyList?(): void;

  reloadFilterData?(): void;

  assignedList?: Array<Uuid>;
  setAssignedList?: (assignedList: Array<Uuid>) => void;
  isHromadna?: boolean;
  withoutNavLinks?: boolean;
  showBreadcrumbsOnly?: boolean;
}

const Layout = ({
  children,
  reloadSubjektBezPripomienokList,
  reloadPripomienkyList,
  reloadFilterData,
  assignedList,
  setAssignedList,
  breadcrumbRouteList = [],
  isHromadna = false,
  withoutNavLinks = false,
  showBreadcrumbsOnly = true,
}: Props): ReactElement => {
  const { pathname } = useLocation();
  const { legislationProcess, status: userContextStatus } = useUserContext();
  const { stadiumMaterialuHistoria, stadiumMaterialuHistoriaStatus } = useStadiumMaterialuHistoriaContext();
  useFetchHistoria();
  // @ts-ignore
  const { type, year, index } = useParams<{ type: string, year: number, index: number }>();
  const { lastVisitedListPage } = useHistoryContext();
  const getPripomienkyListUrl = (): string => (isHromadna
    ? routes.hromadnePripomienky.list.withParams(type, year, index)
    : routes.pripomienky.list.withParams(type, year, index));

  useEffect(() => {
    if (userContextStatus?.finished === true && stadiumMaterialuHistoriaStatus?.finished === true) {
      waitForElement('.idsk-heading-xl').then(() => {
        setTitle(getTitle());
      });
    }
  }, [userContextStatus, stadiumMaterialuHistoriaStatus]);

  const renderBackLink = (): ReactElement => {
    if (withoutNavLinks || !legislationProcess) {
      return <></>;
    }
    if (showBreadcrumbsOnly && pathname !== getPripomienkyListUrl()) {
      return (
        <BackLink
          className="sprava-pripomienok__backlink"
          to={lastVisitedListPage || getPripomienkyListUrl()}
        >Späť na zoznam
        </BackLink>
      );
    }
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
      <div
        id="sprava-pripomienok__backlink-wrapper"
        className="sprava-pripomienok__backlink-wrapper"
        onClick={(e) => {
          e.preventDefault();
          window.parent.postMessage({
            type: 'iframe-to-parent-external-route',
            route: routes.procesy.detail.withParams(type, year, index),
          }, '*');
        }}
      >
        {showBreadcrumbsOnly && (
          <BackLink
            className="sprava-pripomienok__backlink"
            to=""
          >
            {`Späť na ${type}/${year}/${index}`}
          </BackLink>
        )}
      </div>
    );
  };
  return (
    <Content className="sprava-pripomienok-main-container" width="wide">
      <PageLoader loading={((legislationProcess === null || legislationProcess === undefined)
          && userContextStatus?.loading)
        || ((stadiumMaterialuHistoria === null || stadiumMaterialuHistoria === undefined)
          && stadiumMaterialuHistoriaStatus?.loading)}
      >
        <>
          {!withoutNavLinks && (
            <Breadcrumbs
              routeList={[...getBreadcrumbPrefixArray(type, year, index, isHromadna), ...breadcrumbRouteList]}
            />
          )}
          {!hasBeenInStadium(StadiumMaterialuEnum.MPK, stadiumMaterialuHistoria?.historiaRecords)
            ? <NotFoundPage />
            : (
              <>
                <GlobalErrors />
                <ErrorMessages />
                <SuccessMessages />
                {renderBackLink()}
                <PripomienkyNavigation
                  reloadSubjektBezPripomienokList={reloadSubjektBezPripomienokList}
                  reloadPripomienkyList={reloadPripomienkyList}
                  reloadFilterData={reloadFilterData}
                  assignedList={assignedList}
                  setAssignedList={setAssignedList}
                />
                {children}
              </>
            )}
        </>
      </PageLoader>
    </Content>
  );
};

export default Layout;
